<template>
  <div>
    <select
      v-if="editable"
      :required="!column.allowNull"
      :value="selectedIds"
      @change="updateRecordValue($event.target.selectedOptions)"
      :multiple="column.multiple"
      class="px-3 py-3 placeholder-blueGray-300 text-blueGray-600 rounded text-sm focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
      :class="
        transparent ? 'border-0 bg-transparent' : 'border-0 bg-white shadow'
      "
    >
      <option v-if="column.allowNull" class="px-3 py-3"></option>
      <option
        v-for="option in filteredOptions"
        :key="option[primaryKey]"
        :value="option[primaryKey]"
        class="px-3 py-3"
      >
        {{ getValues(option, getLabels(model)) }}
      </option>
    </select>
    <div
      v-else
      :class="
        transparent
          ? ''
          : 'min-h-12 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 rounded text-sm focus:outline-none focus:ring w-full ease-linear transition-all duration-150 bg-gray-100 border border-gray-300'
      "
    >
      {{ selectedValue }}
    </div>
  </div>
</template>
<script>
// Models
import { cargo } from "@/models/cargo";
import { contacto } from "@/models/contacto";
import { cuenta } from "@/models/cuenta";
import { directo_hora } from "@/models/directo_hora";
import { empresa } from "@/models/empresa";
import { feriado } from "@/models/feriado";
import { hora } from "@/models/hora";
import { licencia } from "@/models/licencia";
import { moneda } from "@/models/moneda";
import { obra } from "@/models/obra";
import { oficina } from "@/models/oficina";
import { pago_categoria } from "@/models/pago_categoria";
import { pago_subcategoria } from "@/models/pago_subcategoria";
import { personal } from "@/models/personal";
import { presupuesto } from "@/models/presupuesto";
import { rol } from "@/models/rol";
import { tarea } from "@/models/tarea";
import { transaccion } from "@/models/transaccion";
import { usuario } from "@/models/usuario";

// Services
import CargoService from "@/services/cargo.service";
import ContactoService from "@/services/contacto.service";
import CuentaService from "@/services/cuenta.service";
import Directo_horaService from "@/services/directo_hora.service";
import EmpresaService from "@/services/empresa.service";
import FeriadoService from "@/services/feriado.service";
import HoraService from "@/services/hora.service";
import LicenciaService from "@/services/licencia.service";
import MonedaService from "@/services/moneda.service";
import ObraService from "@/services/obra.service";
import OficinaService from "@/services/oficina.service";
import Pago_categoriaService from "@/services/pago_categoria.service";
import Pago_subcategoriaService from "@/services/pago_subcategoria.service";
import PersonalService from "@/services/personal.service";
import PresupuestoService from "@/services/presupuesto.service";
import RolService from "@/services/rol.service";
import TareaService from "@/services/tarea.service";
import TransaccionService from "@/services/transaccion.service";
import UsuarioService from "@/services/usuario.service";

export default {
  data() {
    return {
      selectOptions: [],
      dataTypes: {
        cargo: {
          model: cargo,
          service: CargoService,
        },
        contacto: {
          model: contacto,
          service: ContactoService,
        },
        cuenta: {
          model: cuenta,
          service: CuentaService,
        },
        directo_hora: {
          model: directo_hora,
          service: Directo_horaService,
        },
        empresa: {
          model: empresa,
          service: EmpresaService,
        },
        feriado: {
          model: feriado,
          service: FeriadoService,
        },
        hora: {
          model: hora,
          service: HoraService,
        },
        licencia: {
          model: licencia,
          service: LicenciaService,
        },
        moneda: {
          model: moneda,
          service: MonedaService,
        },
        obra: {
          model: obra,
          service: ObraService,
        },
        oficina: {
          model: oficina,
          service: OficinaService,
        },
        pago_categoria: {
          model: pago_categoria,
          service: Pago_categoriaService,
        },
        pago_subcategoria: {
          model: pago_subcategoria,
          service: Pago_subcategoriaService,
        },
        personal: {
          model: personal,
          service: PersonalService,
        },
        presupuesto: {
          model: presupuesto,
          service: PresupuestoService,
        },
        rol: {
          model: rol,
          service: RolService,
        },
        tarea: {
          model: tarea,
          service: TareaService,
        },
        transaccion: {
          model: transaccion,
          service: TransaccionService,
        },
        usuario: {
          model: usuario,
          service: UsuarioService,
        },
      },
    };
  },

  props: {
    column: {},
    record: {},
    disabled: {},
    transparent: {},
  },

  computed: {
    editable() {
      return !this.disabled;
    },

    selectedIds() {
      if (!this.column.multiple) {
        return this.record[this.column.name];
      }
      const items = this.record[this.column.name];
      if (!items) return [];
      return items.map((item) =>
        typeof item === "object" ? item[this.primaryKey] : item
      );
    },

    selectedValue() {
      if (this.column?.type === "STRINGINLIST") {
        return this.record[this.column.name];
      }

      const values = this.joinnedRecords?.map((record) => {
        return this.getValues(
          record,
          this.getLabels(this.currentDataType.model)
        );
      });
      return values?.join(", ");
    },

    currentDataType() {
      return this.dataTypes[this.column.type]
        ? this.dataTypes[this.column.type]
        : {
            model: null,
            service: null,
          };
    },

    model() {
      return this.currentDataType.model;
    },

    service() {
      return this.currentDataType.service;
    },

    primaryKey() {
      return this.service?.primaryKey || "id";
    },

    joinnedRecords() {
      return this.column.multiple
        ? this.record[this.column.name]
        : [this.record[this.column.as || this.column.type]];
    },

    filterValue() {
      if (!this.column.filterBy) {
        return null;
      }
      return typeof this.column.filterBy.value === "function"
        ? this.column.filterBy.value(this.record)
        : this.record[this.column.filterBy.value];
    },

    filteredOptions() {
      if (!this.column.filterBy || !this.filterValue || !this.selectOptions) {
        return this.selectOptions;
      }

      const filterField = typeof this.column.filterBy.field === "function"
        ? this.column.filterBy.field(this.record)
        : this.column.filterBy.field;
      return this.selectOptions.filter((option) => {
        return option[filterField] == this.filterValue;
      });
    },
  },

  watch: {
    column: {
      immediate: true,
      handler() {
        this.loadOptions();
      },
    },
    record: {
      immediate: true,
      handler() {
        this.loadOptions();
      },
    },
    disabled: {
      immediate: true,
      handler() {
        this.loadOptions();
      },
    },
  },

  methods: {
    getLabels(model) {
      const columns = model?.filter((column) => column.showAsLabel);
      const labels = columns?.map((column) => column.name);
      return labels || ["id"];
    },

    getValues(record, labels) {
      const values = labels?.map((label) => (record ? record[label] : null));
      return values?.join(" ");
    },

    loadOptions() {
      if (!this.editable) {
        this.selectOptions = [];
        return;
      }

      const sortBy = this.getLabels(this.model)[0];
      const order = "ASC";

      if (this.service) {
        const filters = this.column.filters
          ? JSON.stringify(this.column.filters)
          : undefined;
        this.service
          .list({
            pageSize: 1000,
            filters,
            sortBy,
            order,
          })
          .then((data) => {
            this.selectOptions = data.records;
          })
          .catch((response) => {
            console.log(response);
          });
      } else {
        this.selectOptions = this.column.options?.map((item) => ({ id: item }));
      }
    },

    updateRecordValue(selectedOptions) {
      let selectedValues = [];
      for (let i = 0; i < selectedOptions.length; i++) {
        selectedValues.push(selectedOptions[i].value);
      }
      if (!this.column.multiple) {
        selectedValues = selectedValues.length > 0 ? selectedValues[0] : null;
      }
      this.$emit("pickValue", selectedValues);
    },
  },
};
</script>
